<template>
  <VOnboardingWrapper
    ref="onboardingWrapper"
    :steps="steps"
    :options="options"
    @finish="onFinish"
  >
    <template #default="{ step, next, previous, exit, isFirst, isLast, index }">
      <VOnboardingStep>
        <div
          class="relative bg-white border-[2.5px] border-gray-900 p-4 rounded-xl shadow-lg shadow-gray-900/50"
          :style="{ maxWidth: step.maxWidth ?? '450px' }"
        >
          <div class="flex gap-1 justify-between items-start">
            <h3 class="text-2xl font-semibold text-gray-700">
              {{ step.content?.title }}
            </h3>
            <button
              class="bg-transparent"
              aria-label="Close"
              @click="handleEarlyExit"
            >
              <Xmark class="w-3.5 text-gray-700 cursor-pointer" />
            </button>
          </div>
          <p class="mt-2">{{ step.content?.description }}</p>
          <div class="mt-4 flex justify-between">
            <div>
              <button
                v-if="isFirst && props.page == 'dashboard'"
                @click="skipTour"
                class="w-28 bg-gray-200 hover:bg-gray-300 text-gray-800"
              >
                Skip Tour
              </button>
              <button
                v-if="!isFirst"
                @click="previous"
                class="min-w-24 bg-gray-200 hover:bg-gray-300 text-gray-800"
              >
                Previous
              </button>
            </div>
            <button v-if="!isLast" @click="next" class="min-w-24">Next</button>
            <button v-else @click="next" class="min-w-24">
              {{ props.page == "editor" ? "Finish" : "Try It Now" }}
            </button>
          </div>
          <div
            class="-z-10 absolute inset-0 ping-once rounded-xl bg-gray-300 opacity-75"
          ></div>
        </div>
      </VOnboardingStep>
    </template>
  </VOnboardingWrapper>
</template>

<script setup>
import { useCookies } from "@vueuse/integrations/useCookies";
import { storeToRefs } from "pinia";
import {
  useVOnboarding,
  VOnboardingStep,
  VOnboardingWrapper,
} from "v-onboarding";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import useOnboardingStore from "@/stores/onboarding.js";
import "v-onboarding/dist/style.css";
import Xmark from "@/components/icons/Xmark.vue";

const onboardingStore = useOnboardingStore();
const { shouldStart } = storeToRefs(onboardingStore);

const cookies = useCookies(["csrftoken"]);
const router = useRouter();
const onboardingWrapper = ref(null);
const { start, finish } = useVOnboarding(onboardingWrapper);
const options = {
  popper: {
    modifiers: [{ name: "offset", options: { offset: [0, 16] } }],
  },
  labels: { finishButton: "Next" },
  overlay: {
    padding: 6,
    borderRadius: 8,
  },
};

const props = defineProps({
  page: { type: String, required: true },
});

const steps = computed(() => onboardingSteps[props.page]);

function onFinish() {
  setCookie();
  onboardingStore.end();
}

function startOnboardingIfNew() {
  if (shouldStart.value || !cookies.get(`${props.page}-onboarding-completed`)) {
    start();
  }
}

function setCookie(page = props.page, completedDate = null) {
  if (!completedDate) {
    completedDate = Date.now();
  }
  // set the expiration as far in the future as possible
  const expireDate = new Date("December 31, 9999 23:59:59 GMT");
  cookies.set(`${page}-onboarding-completed`, completedDate, {
    expires: expireDate,
    path: "/",
  });
}

function updateCookieExpiration() {
  // push the cookie expiration date as far back as possible
  const completedDate = cookies.get(`${props.page}-onboarding-completed`);
  if (completedDate) {
    setCookie(props.page, completedDate);
  }
}

function handleEarlyExit() {
  setCookie();
  onboardingStore.end();
  finish();
}

function skipTour() {
  setCookie("dashboard");
  setCookie("search");
  setCookie("domain");
  setCookie("editor");
  onboardingStore.end();
  finish();
}

watch(shouldStart, (shouldStartVal) => {
  if (shouldStartVal) {
    start();
  }
});

onMounted(async () => {
  startOnboardingIfNew();
  updateCookieExpiration();
});

const onboardingSteps = {
  dashboard: [
    {
      attachTo: { element: "#cmp-header" },
      content: {
        title: "Welcome!",
        description:
          "The Content Management Portal (CMP) is a platform to suggest content edits to State Farm agent marketing websites. Let's take a quick tour.",
      },
      options: {
        overlay: { padding: 0, borderRadius: 0 },
      },
    },
    {
      attachTo: { element: "#dashboard-stats" },
      options: {
        popper: {
          placement: "left",
        },
      },
      maxWidth: "250px",
      content: {
        title: "Dashboard Stats",
        description:
          "This section shows the total website and content suggestion statistics for your entire company. This includes all the suggestions created by you and anyone else on your team.",
      },
    },
    {
      attachTo: { element: "#side-nav" },
      content: {
        title: "Side Navigation",
        description:
          "The side nav is the main way to travel around the CMP. More options can appear in this menu depending on which page you're on.",
      },
      options: {
        overlay: { padding: 0, borderRadius: 0 },
      },
    },
    {
      attachTo: { element: "#nav-item-dashboard" },
      content: {
        title: "Dashboard",
        description:
          'You can click "Dashboard" to get back to this page at any time.',
      },
    },
    {
      attachTo: { element: "#nav-item-domains" },
      content: {
        title: "Domain Search",
        description:
          'Click on "Domain Search" to find a domain that you would like to make a suggestion for.',
      },
    },
  ],
  search: [
    {
      attachTo: { element: "#domain-name" },
      content: {
        title: "Domain Search",
        description:
          "Type in the name of a domain and search for it in order to begin creating content suggestions. Once you see the domain you're looking for, click on it to go to its overview page. Try searching for a domain now.",
      },
    },
  ],
  domain: [
    {
      attachTo: { element: "#domain-overview" },
      content: {
        title: "Domain Overview",
        description:
          "This page shows information and stats for a specific domain. There are two main sections to look at.",
      },
      options: {
        popper: {
          placement: "left",
        },
      },
      maxWidth: "250px",
    },
    {
      attachTo: { element: "#domain-info-section" },
      content: {
        title: "Domain Info",
        description:
          "This section provides general information about the domain and agent who owns the domain.",
      },
      options: {
        popper: {
          placement: "left",
        },
      },
      maxWidth: "250px",
    },
    {
      attachTo: { element: "#domain-stats" },
      content: {
        title: "Suggestion Activity",
        description:
          "This section shows content suggestion stats for this domain only. It's a good way to get a picture of its recent content activity.",
      },
      options: {
        popper: {
          placement: "left",
        },
      },
      maxWidth: "250px",
    },
    {
      attachTo: { element: "#editor-nav-items" },
      content: {
        title: "Editor Navigation",
        description:
          "You now have access to more pages since you have selected a specific domain. Click on an editor page to create a suggestion for that content type.",
      },
    },
  ],
  editor: [
    {
      attachTo: { element: "#base-editor" },
      content: {
        title: "Content Editor",
        description:
          "Welcome to an editor page. Editor pages allow you to create suggestions and view history for a specific type of content on a domain. Let's go over the three main sections of this page.",
      },
      options: {
        popper: {
          placement: "left-start",
        },
      },
      maxWidth: "250px",
    },
    {
      attachTo: { element: "#editor-section" },
      content: {
        title: "Content Editor",
        description:
          "The first section is the content editor. You can create new content from scratch or edit existing content here. After entering content, the CMP will automatically check it for compliance and display any changes that need to be made before the content suggestion can be submitted.",
      },
      options: {
        popper: {
          placement: "left",
        },
      },
      maxWidth: "250px",
    },
    {
      attachTo: { element: "#pending-suggestion-list" },
      content: {
        title: "Pending Suggestions",
        description:
          "The second section is the pending suggestion list. This list shows any suggestions that have been created but have not yet been reviewed by the agent.",
      },
      options: {
        popper: {
          placement: "left",
        },
      },
      maxWidth: "250px",
    },
    {
      attachTo: { element: "#content-history-list" },
      content: {
        title: "Content History",
        description:
          "The third section is the content history list. This list shows the history of recent suggestions as well as the content that is currently live on the site.",
      },
      options: {
        popper: {
          placement: "left",
        },
      },
      maxWidth: "250px",
    },
    {
      attachTo: { element: "#editor-section" },
      content: {
        title: "That's it!",
        description: "Now go make some content suggestions!",
      },
    },
  ],
};
</script>

<style scoped>
.ping-once {
  animation: ping-once 1.4s cubic-bezier(0.4, 0, 0.6, 1) 1 forwards;
}

@keyframes ping-once {
  75%,
  100% {
    transform: scaleY(1.16) scaleX(1.08);
    opacity: 0;
  }
}
</style>
